import { truncate, date, isDateInDays, orderStatus, paymentType, paymentStatus, deliveryType, orderDeliveryPeriod, orderDateCreatedColorCode, orderStatusColorCode, orderPaymentTypeColorCode, orderHandoverDeliveryDateColorCode, orderDeliveryTypeColorCode, normalizeItem } from './mapper.js';
import { mappedOrderFields, insuranceStateValues } from './constants';
import * as TimSort from 'timsort';

function isPlainRelationObject(value) {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isEditableByCurrentUser(item, user) {
    if (typeof user != 'undefined' && user != null) {
        return ( user.userid == (item.current_editor != null ? item.current_editor.id : null) );
    }
    return false;
}

export function normalizeCustomer(customer) {
    const empty = {
        id: null,
        firstname: '',
        lastname: '',
        phone: '',
        email: '',
    };
    if (customer === null || typeof customer === 'undefined') {
        return empty;
    }
    if (!isPlainRelationObject(customer)) {
        return empty;
    }
    return customer;
};

function normalizeProcessLocation(location) {
    const empty = {
        id: null,
        short_name: '',
        company: '',
    };
    if (location === null || typeof location === 'undefined') {
        return empty;
    }
    if (!isPlainRelationObject(location)) {
        return empty;
    }
    return location;
};

function resolveLocationShortNameForDisplay(order) {
    const loc = order.location;
    if (isPlainRelationObject(loc) && loc.id != null) {
        if (loc.short_name != null && String(loc.short_name).trim() !== '') {
            return String(loc.short_name).trim();
        }
    }
    const rawFlat = order.location_short_name;
    return rawFlat != null && String(rawFlat).trim() !== ''
        ? String(rawFlat).trim()
        : '';
}

function normalizeResponsibleEmployee(employee) {
    const empty = {
        id: null,
        first_name: '',
        last_name: '',
        role: '',
    };
    if (employee === null || typeof employee === 'undefined') {
        return empty;
    }
    if (!isPlainRelationObject(employee)) {
        return empty;
    }
    return employee;
};

/** When Directus returns only full_name, derive firstname/lastname for mapOrder / search. */
function applyCustomerFullNameFallback(customer) {
    if (!isPlainRelationObject(customer)) {
        return customer;
    }
    const fn = customer.firstname != null ? String(customer.firstname).trim() : '';
    const ln = customer.lastname != null ? String(customer.lastname).trim() : '';
    if (fn || ln) {
        return customer;
    }
    if (customer.full_name == null || String(customer.full_name).trim() === '') {
        return customer;
    }
    const parts = String(customer.full_name).trim().split(/\s+/);
    return {
        ...customer,
        firstname: parts[0] || '',
        lastname: parts.slice(1).join(' ') || '',
    };
}

export function isOpen(order){
    if( order.current_editor != null && typeof order.current_editor != 'undefined' ){
        if( order.current_editor.id != null ){
            return true;
        }
    }
    return false;
}

function getMappedResponsibleEmployee(order){
    if('responsible_employee' in order){
        if( order.responsible_employee != null ) {
            if( 'first_name' in order.responsible_employee && 'last_name' in order.responsible_employee ){
                return `${order.responsible_employee.first_name} ${order.responsible_employee.last_name}`;
            }
        }
    }
    return '';
}

export function mapOrder(order, user) {
    normalizeItem(order, mappedOrderFields);
    order.customer = applyCustomerFullNameFallback(normalizeCustomer(order.customer));
    order.location = normalizeProcessLocation(order.location);
    const locationDisplayShortName = resolveLocationShortNameForDisplay(order);
    order.responsible_employee = normalizeResponsibleEmployee(order.responsible_employee);
    order.current_editor = normalizeResponsibleEmployee(order.current_editor);
    order.payment_amount = order.payment_amount != null ? Number.parseFloat(order.payment_amount).toFixed(2) : null;
    order.no_prescription = order.no_prescription != null ? order.no_prescription : false;
    order.priority = order.priority !== null ? order.priority : null;
    order.on_site_payment = order.on_site_payment !== null ? order.on_site_payment : null;
    order.items = order.items != null ? order.items : [];

    const mappedUserUpdated = getMappedResponsibleEmployee(order);

    const customer_name = `${order.customer.firstname} ${order.customer.lastname}`.toLowerCase()
    //const customer_firstname = `${order.customer.firstname}`.toLowerCase();
    const customer_lastname = `${order.customer.lastname}`.toLowerCase();

    order.search = {
        ext_invoice_number: order.ext_invoice_number,
        customer_name: customer_name,
        customer_email: order.customer != null ? `${order.customer.email}`.toLowerCase() : ''
    };

    order.style = {
        orderDateCreatedColorCode: orderDateCreatedColorCode(order.date_created),
        orderStatusColorCode: orderStatusColorCode(order.status, order.payment_type),
        orderPaymentTypeColorCode: order.on_site_payment === true && order.payment_status !== 'paid' ? '#f5ef42' : order.in_payment_verification === true ? '#FF0000' : ( order.payment_status === 'under_review' ? '#FF0000' : order.payment_status === 'paid' ? '#33a854' : orderPaymentTypeColorCode(order.payment_type) ),
        orderHandoverDeliveryDateColorCode: orderHandoverDeliveryDateColorCode(order.delivery_type, order.handover_delivery_date, order.preferred_date_of_delivery),
        orderDeliveryTypeColorCode: orderDeliveryTypeColorCode(order.delivery_type, order.no_prescription)
    };

    order.sort = {
        responsible_employee_name: mappedUserUpdated.toLowerCase().replace(/\s/g, ""),
        //customer_name: customer_name.replace(/\s/g, ""),
        //customer_firstname: customer_firstname.replace(/\s/g, ""),
        customer_lastname: customer_lastname.replace(/\s/g, ""),
        location_short_name: locationDisplayShortName.toLowerCase(),
        date_of_invoice: new Date(order.date_of_invoice),
        payment_term: new Date(order.payment_term),
        date_of_closure: new Date(order.date_of_closure),
        date_created: new Date(order.date_created).toLocaleString('en-GB').replace(',', '').replace(/\//g, '.'),
        preferred_date_of_delivery: new Date(order.preferred_date_of_delivery),
    };

    order.mapped = {
      responsible_employee: mappedUserUpdated,
      is_open: isOpen(order),
      hasProductExpiry: order.payment_type == 'prepayment' && order.product_expiry != null,
      hasCustomerPhone: orderHasCustomerPhone(order),
      hasCustomerEmail: orderHasCustomerEmail(order),
      isValidCustomer: isValidCustomer(order),
      isEditableByMe: isEditableByCurrentUser(order, user),
      customer_name: `${order.customer.firstname} ${order.customer.lastname}`,
      location_short_name: locationDisplayShortName,
      status: orderStatus(order.status),
      payment_term: date(order.payment_term, 'DD.MM.YYYY'),
      payment_type: paymentType(order.payment_type),
      invoice_type: paymentType(order.invoice_type),
      payment_status: paymentStatus(order.payment_status),
      delivery_type: deliveryType(order.delivery_type),
      delivery_period: orderDeliveryPeriod(order.delivery_period),
      date_of_invoice: date(order.date_of_invoice, 'DD.MM.YYYY'),
      date_created: date(
        order.date_created !== null ? order.date_created : order.date_of_closure,
        'dd DD.MM.YYYY H:mm'
      ),
      date_inbox: date(order.date_inbox, 'dd DD.MM.YYYY H:mm'),
      date_updated: date(order.date_updated, 'dd DD.MM.YYYY H:mm'),
      product_expiry: date(order.product_expiry, 'dd DD.MM.YYYY'),
      preferred_date_of_delivery: date(order.preferred_date_of_delivery, 'dd DD.MM.YYYY'),
      last_comment:
        isPlainRelationObject(order.last_comment) && order.last_comment.content != null
          ? truncate(order.last_comment.content, 96)
          : '',
      comments_total: order.comments_total !== null ? order.comments_total : 0,
      external_order_id: order.external_order_id != null ? order.external_order_id : '',
      id: order.id != null ? order.id : '',
      hasAttachments: Array.isArray(order.files) && order.files.length > 0,
      attachments_count: Array.isArray(order.files) ? order.files.length : 0
    };

    order.location_short_name = locationDisplayShortName;

    order.class = {
        isEditableColor: order.mapped.isEditableByMe === true ? '' : 'text-red',
        isEditable: order.mapped.isEditableByMe === true ? 'editable' : 'not-editable',
    }

    order.marked = false;

    return order;
}

export function getMappedOrders(orders, user) {
    let out = [];
    const length = orders.length-1;
    for(let i = 0; i<= length; i++){
        out[out.length] = mapOrder(orders[i], user);
    }
    return out;
}

export function getFilteredSortedOrders(orders, days, search, sort, isDateInbox = false) {
    let tmp_orders = [];
    if (orders != null && typeof orders != 'undefined') {
        tmp_orders = filterOrdersByDays(orders, days, isDateInbox);
        tmp_orders = searchOrders(tmp_orders, search);
        tmp_orders = sortOrders(tmp_orders, sort);
    }
    return tmp_orders;
}

export function getFilteredSortedOrdersMultiDate(orders, daySelections, search, sort) {
    let tmp_orders = [];
    if (orders != null && typeof orders != 'undefined') {
        tmp_orders = filterOrdersByMultipleDays(orders, daySelections);
        tmp_orders = searchOrders(tmp_orders, search);
        tmp_orders = sortOrders(tmp_orders, sort);
    }
    return tmp_orders;
}

export function getFilteredOrdersMultiDate(orders, daySelections, search) {
    let tmp_orders = [];
    if (orders != null && typeof orders != 'undefined') {
        tmp_orders = filterOrdersByMultipleDays(orders, daySelections);
        tmp_orders = searchOrders(tmp_orders, search);
    }
    return tmp_orders;
}

export function getOnlyFilteredOrders(orders, days, search) {
    let tmp_orders = [];
    if (orders != null && typeof orders != 'undefined') {
        tmp_orders = filterOrdersByDays(orders, days);
        tmp_orders = searchOrders(tmp_orders, search);
    }
    return tmp_orders;
}

function filterOrdersByDays(orders, days, isDateInbox = false) {
  const results = [];
  for (let i = 0; i < orders.length; i++) {
    const dateField = isDateInbox ? orders[i].date_inbox : orders[i].date_created;
    if (isDateInDays(dateField, days)) {
      results.push(orders[i]);
    }
  }

  return results;
}

function filterOrdersByMultipleDays(orders, daySelections) {
  const results = [];
  
  for (let i = 0; i < orders.length; i++) {
    let passesFilter = true;

    const isDateCreatedFilterActive = daySelections.dateCreated && daySelections.dateCreated.length < 7;
    const isDateInboxFilterActive = daySelections.dateInbox && daySelections.dateInbox.length < 7;
    const isPreferredDateFilterActive = daySelections.preferredDateOfDelivery && daySelections.preferredDateOfDelivery.length < 7;

    if (isDateCreatedFilterActive) {
      if (!isDateInDays(orders[i].date_created, daySelections.dateCreated)) {
        passesFilter = false;
      }
    }

    if (passesFilter && isDateInboxFilterActive) {
      if (!isDateInDays(orders[i].date_inbox, daySelections.dateInbox)) {
        passesFilter = false;
      }
    }

    if (passesFilter && isPreferredDateFilterActive) {
      if (!isDateInDays(orders[i].preferred_date_of_delivery, daySelections.preferredDateOfDelivery)) {
        passesFilter = false;
      }
    }

    if (passesFilter) {
      results.push(orders[i]);
    }
  }

  return results;
}

function orderHasCustomerPhone(order) {
    if (!('customer' in order)) {
        return false;
    }
    if (!isPlainRelationObject(order.customer)) {
        return false;
    }
    if ('phone' in order.customer) {
        return order.customer.phone != null ? (String(order.customer.phone).trim() !== '') : false;
    }
    return false;
}

function orderHasCustomerEmail(order) {
    if (!('customer' in order)) {
        return false;
    }
    if (!isPlainRelationObject(order.customer)) {
        return false;
    }
    if ('email' in order.customer) {
        return order.customer.email != null ? (String(order.customer.email).trim() !== '') : false;
    }
    return false;
}

function isValidCustomer(order) {
    if (!('customer' in order)) {
        return false;
    }
    if (!isPlainRelationObject(order.customer)) {
        return false;
    }
    if ('firstname' in order.customer && 'lastname' in order.customer) {
        return true;
    }
    return false;
}

export function sortOrders(orders, sort) {
    if (sort.key != '' && sort.order != '') {
        if (['payment_amount', 'orders_id'].includes(sort.key)) {
            if (sort.order === 'ASC') {
                TimSort.sort(orders, (a, b) => { return compareByKeyAsc(a, b, sort.key) });
            } else {
                TimSort.sort(orders, (a, b) => { return compareByKeyDesc(a, b, sort.key) });
            }
        } else if (["customer_name",'customer_firstname','customer_lastname', "responsible_employee_name", 'location_short_name',"date_created", 'date_of_invoice', 'payment_term', 'date_of_closure', "preferred_date_of_delivery"].includes(sort.key)) {
            if (sort.order === 'ASC') {
                TimSort.sort(orders, (a, b) => { return compareBySortdAttributeAsc(a, b, sort.key) });
            } else {
                TimSort.sort(orders, (a, b) => { return compareBySortAttributeDesc(a, b, sort.key) });
            }
        } else if (['status', "payment_type", 'payment_status', 'delivery_type', 'delivery_period'].includes(sort.key)) {
            if (sort.order === 'ASC') {
                TimSort.sort(orders, (a, b) => { return compareByMappedAttributeAsc(a, b, sort.key) });
            } else {
                TimSort.sort(orders, (a, b) => { return compareByMappedAttributeDesc(a, b, sort.key) });
            }
        }  else if (["ext_invoice_number"].includes(sort.key)) {
            if (sort.order === 'ASC') {
                TimSort.sort(orders, (a, b) => { return compareStringAsc(a, b, sort.key) });
            } else {
                TimSort.sort(orders, (a, b) => { return compareStringDesc(a, b, sort.key) });
            }
        } else {
            if (sort.order === 'ASC') {
                TimSort.sort(orders, (a, b) => { return compareByKeyAsc(a, b, sort.key) });
            } else {
                TimSort.sort(orders, (a, b) => { return compareByKeyDesc(a, b, sort.key) });
            }
        }
    }
    return orders;
}

function compareStringAsc(a, b, key) {
    if (a[key] == null) return -1;
    return a[key].localeCompare(b[key]);
}

function compareStringDesc(a, b, key) {
    if (b[key] == null) return 1;
    return -1 * a[key].localeCompare(b[key]);
}

function compareByKeyAsc(a, b, key) {
    if (a[key] == null) return -1;
    if (b[key] == null) return 1;
    if (a[key] < b[key]) return -1;
    if (a[key] > b[key]) return 1;
    return 0;
}

function compareByKeyDesc(a, b, key) {
    if (a[key] == null) return 1;
    if (b[key] == null) return -1;
    if (a[key] > b[key]) return -1;
    if (a[key] < b[key]) return 1;
    return 0;
}

function compareBySortdAttributeAsc(a, b, key) {
    if (
        key == 'customer_name' &&
        a.customer.firstname != null &&
        a.customer.lastname != null &&
        b.customer.firstname != null &&
        b.customer.lastname != null
    ) {
        let x = `${a.customer.lastname} ${a.customer.firstname}`.toLowerCase();
        let y = `${b.customer.lastname} ${b.customer.firstname}`.toLowerCase();
        return x.localeCompare(y);
    }

    if (['date_created', 'date_of_invoice', 'date_of_closure', 'preferred_date_of_delivery'].includes(key)) {
        const dateA = parseDate(a.sort[key]);
        const dateB = parseDate(b.sort[key]);
        if (dateA == null) return -1;
        if (dateB == null) return 1;
        return dateA - dateB;
    }

    if (a.sort[key] == null) return -1;
    if (b.sort[key] == null) return 1;
    if (a.sort[key] < b.sort[key]) return -1;
    if (a.sort[key] > b.sort[key]) return 1;
    return 0;
}

function compareBySortAttributeDesc(a, b, key) {
    if (
        key == 'customer_name' &&
        a.customer.firstname != null &&
        a.customer.lastname != null &&
        b.customer.firstname != null &&
        b.customer.lastname != null
    ) {
        let x = `${a.customer.lastname} ${a.customer.firstname}`.toLowerCase();
        let y = `${b.customer.lastname} ${b.customer.firstname}`.toLowerCase();
        return y.localeCompare(x);
    }

    if (['date_created', 'date_of_invoice', 'date_of_closure', 'preferred_date_of_delivery'].includes(key)) {
        const dateA = parseDate(a.sort[key]);
        const dateB = parseDate(b.sort[key]);
        if (dateA == null) return -1;
        if (dateB == null) return 1;
        return dateB - dateA;
    }

    if (a.sort[key] == null) return -1;
    if (b.sort[key] == null) return 1;
    if (a.sort[key] < b.sort[key]) return 1;
    if (a.sort[key] > b.sort[key]) return -1;
    return 0;
}

function parseDate(dateStr) {
    if (!dateStr) return null;
    if (dateStr instanceof Date) {
        return Number.isNaN(dateStr.getTime()) ? null : dateStr;
    }

    const raw = String(dateStr).trim();

    // Supports "DD.MM.YYYY HH:mm" and "DD.MM.YYYY HH:mm:ss"
    const localMatch = raw.match(
        /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );
    if (localMatch) {
        const [, dd, mm, yyyy, hh, min, sec] = localMatch;
        return new Date(
            Number(yyyy),
            Number(mm) - 1,
            Number(dd),
            Number(hh),
            Number(min),
            Number(sec || 0)
        );
    }

    // Backward-compatible legacy display format:
    // "<weekday> DD.MM.YYYY HH:mm[:ss]" (e.g. "Mo 27.04.2026 13:45")
    const legacyMatch = raw.match(
        /^[^\d\s]+\s+(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );
    if (legacyMatch) {
        const [, dd, mm, yyyy, hh, min, sec] = legacyMatch;
        return new Date(
            Number(yyyy),
            Number(mm) - 1,
            Number(dd),
            Number(hh),
            Number(min),
            Number(sec || 0)
        );
    }

    // Fallback for ISO strings and browser-parseable formats
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareByMappedAttributeAsc(a, b, key) {
    if (a.mapped[key] == null) return -1;
    if (b.mapped[key] == null) return 1;
    if (a.mapped[key] < b.mapped[key]) return -1;
    if (a.mapped[key] > b.mapped[key]) return 1;
    return 0;
}

function compareByMappedAttributeDesc(a, b, key) {
    if (a.mapped[key] == null) return 1;
    if (b.mapped[key] == null) return -1;
    if (a.mapped[key] > b.mapped[key]) return -1;
    if (a.mapped[key] < b.mapped[key]) return 1;
    return 0;
}

export function searchOrders(orders, search) {
    const needle = `${search.term}`.toLowerCase();

    if (['customer_name', 'customer_email','ext_invoice_number'].includes(search.termOption.value) && needle != '') {
        const termOption = search.termOption.value;
        const searchResults = [];
        const ordersLength = orders.length-1;

        for(let i=0; i<=ordersLength; i++){
            if( orders[i].search[termOption].includes(needle) ){
                searchResults[searchResults.length] = orders[i];
            }
        }

        return searchResults;
    }

    return orders;
}

/**
 * Remove all filters and sort property
 */
export function clearActiveFiltersProperties( page ) {
    if ( localStorage.getItem(`page_filters_${page}`) ) {
        localStorage.setItem(`page_filters_${page}`, JSON.stringify({}));
        localStorage.removeItem(`page_sort_${page}`);
    }
}

/**
 * Set active filter for property
 */
export function setActiveFilterProperty(page, filter, value) {
    if ( localStorage.getItem(`page_filters_${page}`) === null ) {
        localStorage.setItem(`page_filters_${page}`, JSON.stringify({}));
    }

    let page_filters_storage = JSON.parse( localStorage.getItem(`page_filters_${page}`) );

    page_filters_storage[filter] = value;

    localStorage.setItem( `page_filters_${page}`, JSON.stringify( page_filters_storage ) );
}

/**
 * Set active sort property
 */
export function setActiveSortProperty( page, key, order ) {
    localStorage.setItem(`page_sort_${page}`, JSON.stringify({
        key: key,
        order: order
    }));
}

/**
 * Get insurance state name
 */
export function getInsuranceStateName(val) {
    if (!val) return 'n.a.';
    const findValueByValue = (valueToFind) => (insuranceStateValues.find(item => item.value === valueToFind) || {}).label;
    return findValueByValue(val);
}

/**
 * Get client orders count
 */
export async function getClientOrdersCount(id) {
    if (!id) return 0;

    let client_filters = {
        _and: [
            {
                customer: {
                    _eq: id
                }
            },
            {
                status: {
                    _nin: ['cancellation', 'draft']
                }
            },
            {
                archived: {
                    _eq: false
                }
            }
        ]
    };

    let token = localStorage.getItem("crm_auth_token");
    let path = `items/orders?aggregate[count]=*&filter=${JSON.stringify(client_filters)}`;
    let result = await fetch(`${process.env.CRM_URL}${path}`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Authorization" : `Bearer ${token}`,
        },
    });

    let data = await result.json();

    if (Array.isArray(data.data) && data.data.length > 0) {
        if (typeof data.data[0].count === 'string' && !isNaN(data.data[0].count)) {
            return data.data[0].count;
        }
    }

    return 0;
}

/**
 * Get inbox for order by id
 */
export async function getOrderInbox(id) {
    let inbox_data;
    let inbox_filters = {
        _and: [
            {
                    id: id,
            }
        ]
    };

    let token = localStorage.getItem("crm_auth_token");
    let path = `items/orders?&filter=${JSON.stringify(inbox_filters)}`;
    let headers = {
        "Content-Type": "application/json",
        "Authorization" : `Bearer ${token}`,
    };
    let result = await fetch(`${process.env.CRM_URL}${path}`, {
        method: "GET",
        headers: headers,
    });
    let data = await result.json();

    if ( ! data.data[0] != null ) {
        inbox_data = data.data[0];

        let inboxFilter = { _and: [{ order: id }] };
        await fetch(`${process.env.CRM_URL}items/inbox?&filter=${JSON.stringify(inboxFilter)}`, {
            method: "GET",
            headers: headers,
        })
        .then((res) => res.json())
        .then((res) => {
            if ( res.data ) {
                inbox_data['message'] = res.data[0]?.message;
            }
        })
        .catch((err) => console.log(err));

        return inbox_data;
    }
    return false;
}

export async function getInboxFiles(inbox_files) {
    let token = localStorage.getItem("crm_auth_token");
    let headers = {
        "Content-Type": "application/json",
        "Authorization" : `Bearer ${token}`,
    };
    let files = [];

    if (inbox_files != null) {
        let files_promises = inbox_files.map(async (file, index) => {
            let file_path = `items/orders_files/${file}`;
            let file_result = await fetch(`${process.env.CRM_URL}${file_path}`, {
                method: "GET",
                headers: headers
            });
            let file_data = await file_result.json();

            if (file_data.data != null) {
                let full_file_path = `files/${file_data.data.directus_files_id}`;
                let full_file_result = await fetch(`${process.env.CRM_URL}${full_file_path}`, {
                    method: "GET",
                    headers: headers
                });
                let full_file_data = await full_file_result.json();
                files.push({
                    id: full_file_data.data.id,
                        index: index,
                    filename_download: full_file_data.data.filename_download,
                });
            }
        });
        await Promise.all(files_promises);
    }
    return files;
}

export function getKeyArrVal(arr, val) {
    const findValueByValue = (valueToFind) => (arr.find(item => item.value === valueToFind) || {}).label;
    return findValueByValue(val);
}
